import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../css/UnitList.css';

const RoomTypeList = () => {
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [deleteModal, setDeleteModal] = useState({ show: false, roomType: null });
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL || "";

  useEffect(() => {
    fetchRoomTypes();
  }, []);

  const fetchRoomTypes = async () => {
    try {
      const response = await fetch(`${API_URL}list-room-types.php`);
      const data = await response.json();

      if (data.status === 'success') {
        setRoomTypes(data.room_types || []);
      } else {
        setError(data.message || 'Failed to fetch room types');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 3000);
  };

  const handleEdit = (roomType) => {
    navigate("/add-room-type", { state: { roomType, isEditing: true } });
    showToast(`Editing room type: ${roomType.type}`, "info");
  };

  const handleDelete = (roomType) => {
    setDeleteModal({ show: true, roomType });
  };

  const confirmDelete = async () => {
    const { roomType } = deleteModal;
    try {
      const response = await fetch(`${API_URL}delete-room-type.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id: roomType.id }),
      });

      const data = await response.json();

      if (data.status === "success") {
        showToast(
          `Room type "${roomType.type}" deleted successfully`,
          "success"
        );
        setRoomTypes((prevRoomTypes) =>
          prevRoomTypes.filter((rt) => rt.id !== roomType.id)
        );
      } else {
        showToast(data.message || "Failed to delete room type", "error");
      }
    } catch (error) {
      showToast("Network error. Could not delete room type.", "error");
    }
    setDeleteModal({ show: false, roomType: null });
  };

  const cancelDelete = () => {
    setDeleteModal({ show: false, roomType: null });
  };

  const handleAddNew = () => {
    navigate('/add-room-type');
  };

  if (loading) {
    return (
      <div className="unit-list-content">
        <div className="loading">Loading room types...</div>
      </div>
    );
  }

  return (
    <div className="unit-list-content">
      <div className="page-header">
        <div className="header-content">
          <h1>Room Type List</h1>
          <p>Manage all room types in your system</p>
        </div>
        <button onClick={handleAddNew} className="add-unit-button">
          Add New Room Type
        </button>
      </div>

      {error && (
        <div className="message error">
          {error}
        </div>
      )}

      {roomTypes.length === 0 ? (
        <div className="no-data">
          No room types found. <button onClick={handleAddNew} className="link-btn">Add your first room type</button>
        </div>
      ) : (
        <div className="unit-list-table-container">
          <table className="unit-list-table">
            <thead>
              <tr>
                <th>SL</th>
                <th>Room Type Name</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {roomTypes.map((roomType, index) => (
                <tr key={roomType.id}>
                  <td>{index + 1}</td>
                  <td>{roomType.type}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => handleEdit(roomType)}
                        className="action-btn edit"
                        title="Edit"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(roomType)}
                        className="action-btn delete"
                        title="Delete"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Toast Notifications */}
      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
            <button
              className="toast-close"
              onClick={() => setToast({ show: false, message: "", type: "" })}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Confirm Delete</h3>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to delete room type{" "}
                <strong>"{deleteModal.roomType?.type}"</strong>?
              </p>
              <p>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={cancelDelete}>
                No, Cancel
              </button>
              <button className="btn btn-danger" onClick={confirmDelete}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomTypeList; 